<template>
  <a class="ArticlePreview" :href="articlePath" :target="content.isExternalArticle && '_blank'">
    <article>
      <SanityImageBackground v-if="content.previewImage || content.image || defaultImage"
        class="image"
        :asset="(content.previewImage || content.image || defaultImage).asset"
      />
      <div class="text">
        <Dateline
          v-if="showDate && content.date"
          class="date"
          :date="content.date"
          :locale="content.locale"
        />
        <h2 class="title">{{ content.title }}</h2>
        <div v-if="content.intro" class="intro">{{ content.intro }}</div>
        <span class="cta">
          {{ content.previewCtaText || localeStrings.readMore }}
          <span class="arrow" v-html="arrowRightSvg" />
        </span>
        <div v-if="content.externalSource" class="externalSource" :title="content.externalSource">
          {{ localeStrings.sourceSource.replace('{source}', content.externalSource)}}
        </div>
      </div>
    </article>
  </a>
</template>

<script>
export const articlePreviewQueryFragment = `
  {
    _createdAt,
    _id,
    _type,
    locale,
    previewImage,
    image,
    date,
    title,
    "category": category->slug.current,
    intro,
    isExternalArticle,
    isLinkToInternalPage,
    "slug": slug.current,
    externalSource,
    url,
    previewCtaText,
  }
`
</script>

<script setup>
import { inject } from 'vue'

import arrowRightSvg from '~/assets/ui/arrow-right.svg?raw'
import { getResourceArticlePath } from '../lib/article-util'

import Dateline from './Dateline.vue'

const localeStrings = inject('localeStrings')
const context = inject('context')

const { content, defaultImage } = defineProps({
  content: {
    type: Object,
    required: true,
  },
  defaultImage: {
    type: Object,
  },
})

// We currently only want to show the date for news items
const showDate = content._type === 'newsItem'
const articlePath = getResourceArticlePath(content, context.articlePathPattern)
</script>

<style scoped>
.ArticlePreview {
  display: block;
  border: thin solid #eee;
  border-radius: 4px;
  overflow: hidden;
}

.image {
  padding-top: 55%;
  background-position: center;
}

.text {
  padding: var(--spacing-6) var(--spacing-5);
  background-color: var(--bg-color-white);
}

.date {
  font-weight: 400;
  font-size: 0.75rem;
  color: var(--text-color-black);
}

.title, .intro {
  /* These rules are required for -webkit-line-clamp */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow-y: hidden;
}

.title {
  margin-top: var(--spacing-1);
  font-weight: 500;
  font-size: var(--font-size-2);
  line-height: 1.5;
  -webkit-line-clamp: 2;
}

.intro {
  margin-top: var(--spacing-1);
  font-weight: 400;
  font-size: var(--font-size-1);
  line-height: 1.5;
  color: var(--text-color-dark);
  -webkit-line-clamp: 3;
}

.cta {
  margin-top: var(--spacing-5);
  display: inline-block;
  font-weight: 400;
  font-size: var(--font-size-1);
  line-height: 1;
  color: var(--brand-color);
}

.cta .arrow {
  margin-left: 0.75em;
  display: inline-block;
  vertical-align: middle;
  position: relative;
  left: 0;
  transition: left 0.15s;
}

.cta:hover .arrow {
  left: 4px;
}

.externalSource {
  margin-top: var(--spacing-1);
  font-weight: 400;
  font-size: 0.625rem;
  color: #8a8a8a;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
